import { Link } from '@tanstack/react-router'

import type { SalesOrder } from '@/lib/api/sales-order-api'
import { PendingAgeLabel } from '@/components/orders/einvoice-pending-age-label'
import { orderHandoverAt } from '@/lib/einvoice-handover'
import { OrderLotDebtBadge } from '@/components/orders/order-lot-debt-badge'
import { EInvoiceStatusBadge } from '@/components/sections/einvoice-status-badge'
import { OrderChannelBadge } from '@/components/sections/order-channel-badge'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { PaymentStatusBadge } from '@/components/sections/payment-status-badge'
import { SLABadge } from '@/components/sla-badge'
import { TableCell, TableRow } from '@/components/ui/table'
import { displayPhone, formatDatePaddedVN, formatVnd } from '@/lib/format'

interface Props {
    order: SalesOrder
    onTrackOpen: () => void
    pendingList?: boolean
}

/** Một dòng đơn trong bảng desktop. */
export function OrdersListTableRow({ order, onTrackOpen, pendingList }: Props) {
    const handoverAt = orderHandoverAt(order)
    return (
        <TableRow>
            <TableCell>
                <Link
                    to="/orders/$orderId"
                    params={{ orderId: order.id }}
                    search={pendingList ? { from: 'pending_list' } : undefined}
                    onClick={onTrackOpen}
                    className="font-mono text-xs text-primary hover:underline"
                >
                    {order.order_code}
                </Link>
                {pendingList && handoverAt ? (
                    <div>
                        <PendingAgeLabel deliveredAt={handoverAt} />
                    </div>
                ) : null}
            </TableCell>
            <TableCell className="font-mono text-xs">
                {order.internal_order_code || (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell>
                {order.customer_id ? (
                    <div>
                        <Link
                            to="/customers/$customerId"
                            params={{ customerId: order.customer_id }}
                            search={{ tab: 'info' }}
                            className="font-medium text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary"
                        >
                            {order.customer_name}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                            {displayPhone(order.customer_phone)}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="font-medium">{order.customer_name}</div>
                        <div className="text-xs text-muted-foreground">
                            {displayPhone(order.customer_phone)}
                        </div>
                    </div>
                )}
            </TableCell>
            <TableCell>
                <OrderChannelBadge channel={order.source_channel} />
            </TableCell>
            <TableCell className="font-medium tabular-nums">
                {formatVnd(Math.round(order.total_amount))}
            </TableCell>
            <TableCell className="tabular-nums">
                {formatVnd(Math.round(order.paid_amount))}
            </TableCell>
            <TableCell>
                <PaymentStatusBadge status={order.payment_status} short />
            </TableCell>
            <TableCell>
                <div className="flex flex-col items-start gap-0.5">
                    <OrderStatusBadge status={order.status} />
                    {/* Đơn của hãng ngoài đứng cùng trạng thái với đơn tự giao;
                        tên hãng là thứ duy nhất phân biệt được khi nhìn danh sách. */}
                    {order.shipping_carrier_is_external && (
                        <span className="text-[11px] leading-tight text-muted-foreground">
                            {order.shipping_carrier_name}
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <OrderLotDebtBadge debt={order.lot_debt} />
            </TableCell>
            <TableCell>
                <EInvoiceStatusBadge
                    orderId={order.id}
                    status={order.einvoice_status}
                    issueEInvoice={order.issue_einvoice}
                />
            </TableCell>
            <TableCell>
                <SLABadge
                    deadlineAt={order.sla_deadline_at}
                    breached={order.sla_breached}
                    warningAt={order.sla_warning_at}
                />
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
                {formatDatePaddedVN(order.created_at)}
            </TableCell>
        </TableRow>
    )
}
